import { NotOnMobile, OnMobile, useMobile } from "lib/@components/mobile"
import { useScheduleSettings } from "routes/schedule/lib/useScheduleSettings"
import { UpBar } from "routes/schedule/components/up-bar"
import { Box } from "@mui/material"
import { ScheduleTitle } from "routes/schedule/scheduleTitle"
import { ScheduleBottom, ScheduleIntroduction, ScheduleTopmost } from "routes/schedule/scheduleIntroduction"
import {
    ScheduleIntroductionTitleBottom,
    ScheduleIntroductionTitleTop,
    ScheduleRightBar,
    ScheduleSummaryAdaptor,
    SelectorTitleExtras,
    TaskListWrapper,
    TaskRowEnd,
} from "slot-definitions"
import { makeLazy } from "lib/make-lazy"
import { indexTasksInSchedule } from "routes/schedule/lib/index-tasks-in-schedule"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useTranslationContext } from "routes/schedule/plugins/translation/use-translation-context"
import { NotInPdf } from "lib/@components/pdf/not-in-pdf"
import { InPdf } from "lib/@components/pdf/in-pdf"
import { PrintableStack } from "lib/@components/pdf/printable-stack"
import { memo, useState } from "react"
import { Bound } from "lib/@components/binding/Bound"
import { ChangeView, HighlightAudit, RefreshSchedules, SelectableChanged } from "event-definitions"
import { noChange } from "lib/@hooks/useRefresh"
import { Watermark } from "lib/@components/watermark/watermark"
import { generate } from "library/guid/index.mjs"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { SchedulePDFDisplay } from "./schedule-pdf-display"
import { RetiredChip } from "routes/facilities/plugins/retired-schedules/retired-schedules.runtime"
import { useIsRetired } from "lib/@hooks/use-is-retired"
import { getAuditsByReferenceIdAndType } from "routes/audit/controller/get-audits-by-reference-id"
import { JustifyChangeButton } from "./edit/justify-change-button"
import { JustifyIntroductionChanged } from "./justifiy-introduction-changed"
import { useComparisonSchedule } from "./edit/useComparisonSchedule"
import {
    resolveScheduleRightBarMode,
    resolveScheduleRightBarWidth,
} from "routes/schedule/lib/right-bar-layout-presenters"
import { HealTaskDeletionAudits } from "routes/schedule/edit/heal-task-deletion-audits.js"
import { If } from "lib/@components/if.js"

export const TaskList = makeLazy(() => import("routes/schedule/tasks/taskList"), "TaskList")
SelectorTitleExtras.plug(<RetiredChip />)

export const ScheduleDetails = memo(function ScheduleDetails() {
    const [id] = useState(generate)
    const [selections] = useState({})
    const [flashRejection, setFlashRejection] = useState("")
    const isMobile = useMobile()
    const [{ onePage }] = useScheduleSettings()
    const { schedule, isPDF, isAuthoringScheduleEditor, isPreview, editMode } = useBoundContext()
    const { loading: translationLoading, rtl: translationRtl } = useTranslationContext()
    const { usesInlineRightBar, usesSideRightBar } = resolveScheduleRightBarMode({ isAuthoringScheduleEditor, onePage })
    const rightBarWidth = resolveScheduleRightBarWidth({ isAuthoringScheduleEditor })
    indexTasksInSchedule(schedule)
    const [schedulePrint] = useSearchParam("schedulePrint")
    SelectableChanged.useRefresh(noChange)
    RefreshSchedules.useRefresh(noChange)
    const isRetired = useIsRetired()
    const referenceId = `${schedule?.id}-version:${parseInt(schedule?.version) + 1}`
    const draftData = getAuditsByReferenceIdAndType.useResults(referenceId, "customSchedule", {
        shortDescription: "New Draft Created",
    })
    const draftId = draftData && draftData[0]?.id
    const [compare] = useComparisonSchedule() ?? []

    TaskRowEnd.usePlug(<JustifyChangeButton priority={210} />)
    ScheduleIntroductionTitleTop.usePlug(<JustifyChangeButton priority={210} />)
    ScheduleIntroductionTitleBottom.usePlug(<JustifyIntroductionChanged />)
    HighlightAudit.handle((id) => {
        setFlashRejection(id)
    })

    return !schedulePrint ? (
        <Bound
            allSelected={selections}
            key={id}
            referenceId={referenceId}
            draftId={draftId}
            compare={compare}
            flashRejection={flashRejection}
            setFlashRejection={setFlashRejection}
        >
            {isRetired && <Watermark text="Retired" />}
            <If truthy={draftId && editMode}>
                <HealTaskDeletionAudits schedule={schedule} referenceId={referenceId} draftId={draftId} />
            </If>
            <PrintableStack
                {...getPreviewScrollProps()}
                key={schedule?._id}
                sx={{ height: 1, flex: 1, minHeight: 0, overflowY: "auto" }}
            >
                <NotInPdf>
                    <UpBar inContainer={true} />
                </NotInPdf>
                <NotOnMobile>
                    <PrintableStack
                        sx={{
                            flex: 1,
                            alignItems: "stretch",
                            overflow: "hidden",
                            flexGrow: 1,
                            mx: isMobile ? -1.5 : 0,
                            ...getPreviewDesktopContentSx(),
                        }}
                        direction="row"
                        spacing={0}
                    >
                        <Box
                            dir={translationRtl ? "rtl" : undefined}
                            className="schedule-details styled-bullets"
                            sx={{
                                overflowX: "hidden",
                                paddingInlineEnd: !isPDF ? 8 : 0,
                                width: isMobile ? `calc(100% + 30px)` : !isPDF ? `calc(100% + 24px)` : "100%",
                                display: "flex",
                                flexDirection: "column",
                                opacity: translationLoading ? 0.4 : 1,
                                transition: "opacity 0.3s ease",
                                pointerEvents: translationLoading ? "none" : undefined,
                            }}
                        >
                            <ScheduleTitle schedule={schedule} />
                            <ScheduleTopmost.Slot schedule={schedule} />
                            <NotInPdf>
                                {!!usesInlineRightBar && (
                                    <ScheduleSummaryAdaptor.Wrapper>
                                        <ScheduleRightBar.Slot Container={<Box className="one-page" />} />
                                    </ScheduleSummaryAdaptor.Wrapper>
                                )}
                            </NotInPdf>
                            <ChangeView.Refresh>
                                {() =>
                                    Object.values(selections).filter(Boolean).length === 0 && (
                                        <ScheduleIntroduction schedule={schedule} />
                                    )
                                }
                            </ChangeView.Refresh>
                            <TaskListWrapper.Wrapper>
                                <TaskList schedule={schedule} />
                            </TaskListWrapper.Wrapper>
                            <ScheduleBottom.Slot schedule={schedule} />
                            <InPdf>
                                <Box className="page-break" />
                                <ScheduleSummaryAdaptor.Wrapper>
                                    <ScheduleRightBar.Slot Container={<Box className="one-page" />} />
                                </ScheduleSummaryAdaptor.Wrapper>
                            </InPdf>
                            <NotInPdf>{!isPreview && <Box mt={50} />}</NotInPdf>
                        </Box>
                        <NotInPdf>
                            {!!usesSideRightBar && (
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                        flexShrink: 0,
                                        maxWidth: rightBarWidth.maxWidth,
                                        width: rightBarWidth.width,
                                        pl: 1,
                                        minWidth: rightBarWidth.minWidth,
                                        minHeight: 0,
                                        display: "flex",
                                        flexDirection: "column",
                                        overflow: "hidden",
                                    }}
                                >
                                    <ScheduleSummaryAdaptor.Wrapper>
                                        <ScheduleRightBar.Slot />
                                    </ScheduleSummaryAdaptor.Wrapper>
                                </Box>
                            )}
                        </NotInPdf>
                    </PrintableStack>
                </NotOnMobile>
                <OnMobile>
                    <Box
                        dir={translationRtl ? "rtl" : undefined}
                        className="styled-bullets"
                        sx={{
                            flexGrow: 1,
                            overflowX: "hidden",
                            overflowY: "scroll",
                            width: "100%",
                            display: "flex",
                            opacity: translationLoading ? 0.4 : 1,
                            transition: "opacity 0.3s ease",
                            pointerEvents: translationLoading ? "none" : undefined,
                            flexDirection: "column",
                        }}
                    >
                        <ScheduleTitle schedule={schedule} />
                        <ScheduleSummaryAdaptor.Wrapper>
                            <ScheduleRightBar.Slot Container={<Box />} />
                        </ScheduleSummaryAdaptor.Wrapper>
                        <ScheduleTopmost.Slot schedule={schedule} />
                        <ChangeView.Refresh>
                            {() =>
                                Object.values(selections).filter(Boolean).length === 0 && (
                                    <ScheduleIntroduction schedule={schedule} />
                                )
                            }
                        </ChangeView.Refresh>
                        <TaskListWrapper.Wrapper>
                            <TaskList schedule={schedule} />
                        </TaskListWrapper.Wrapper>
                        <ScheduleBottom.Slot schedule={schedule} />
                    </Box>
                </OnMobile>
                <InPdf>
                    <Box className="pdf-content-loaded" />
                </InPdf>
            </PrintableStack>
        </Bound>
    ) : (
        <SchedulePDFDisplay schedule={schedule} id={id} />
    )

    function getPreviewScrollProps() {
        if (!isPreview) return {}
        return { "data-cy": "schedule-preview-scroll" }
    }

    function getPreviewDesktopContentSx() {
        if (!isPreview) return {}
        return { flex: "0 0 auto", flexGrow: 0, overflow: "visible" }
    }
})
